import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ImpersonationContextValue = {
  impersonatedUserId: string | null;
  impersonatedEmail: string | null;
  isImpersonating: boolean;
  startImpersonation: (userId: string, email: string) => void;
  stopImpersonation: () => void;
};

const STORAGE_KEY = 'sf_web_impersonation';

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        // Vérifier que la chaîne n'est pas vide et commence par { ou [
        const trimmed = raw.trim();
        if (trimmed && (trimmed.startsWith('{') || trimmed.startsWith('['))) {
          const parsed = JSON.parse(trimmed);
          setImpersonatedUserId(parsed.userId || null);
          setImpersonatedEmail(parsed.email || null);
        } else {
          // Données corrompues, les supprimer
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn('Erreur lors du parsing des données d\'impersonation:', error);
      // Supprimer les données corrompues
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const startImpersonation = useCallback((userId: string, email: string) => {
    setImpersonatedUserId(userId);
    setImpersonatedEmail(email);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, email }));
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent('suivifinance:impersonation-changed', { detail: { userId, email } }));
    } catch {
      // ignore
    }
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUserId(null);
    setImpersonatedEmail(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent('suivifinance:impersonation-changed', { detail: { userId: null, email: null } }));
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<ImpersonationContextValue>(() => ({
    impersonatedUserId,
    impersonatedEmail,
    isImpersonating: !!impersonatedUserId,
    startImpersonation,
    stopImpersonation,
  }), [impersonatedUserId, impersonatedEmail, startImpersonation, stopImpersonation]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within ImpersonationProvider');
  return ctx;
}

// Utility to be consumed by non-React modules (services)
export function getImpersonatedUserIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    
    // Vérifier que la chaîne n'est pas vide et commence par { ou [
    const trimmed = raw.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      // Données corrompues, les supprimer
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    const parsed = JSON.parse(trimmed);
    return parsed.userId || null;
  } catch (error) {
    console.warn('Erreur lors du parsing des données d\'impersonation:', error);
    // Supprimer les données corrompues
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}


